import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import { EntityPicker } from "../../shared/components/EntityPicker";
import { AppModal } from "../../shared/components/AppModal";
import { PagedListControls } from "../../shared/components/PagedListControls";
import { PageLoading } from "../../shared/components/PageLoading";
import { listParams, SortableHeaders, useUrlSortedRows } from "../../shared/components/SortableTableHeader";
import { IntegrationProtectionBoundary } from "../../shared/integrations/IntegrationManagedField";
import { useIntegrationProtection } from "../../shared/integrations/useIntegrationProtection";
import { getPreferredPageSize, rememberPageSize } from "../../shared/pagination/pageSizePreference";
import { getSubcontracting, subcontractingCommand } from "./subcontractingApi";
import type {
  ExternalOrder,
  ExternalOrderHeader,
  ExternalOrderStatus,
  ExternalOrderLine,
  SubcontractingSnapshot,
  Supplier,
  SupplierArticle,
} from "./types";
type Tab = "orders" | "suppliers";
export type SubcontractingSection = "overview" | Tab;
const statuses: ExternalOrderStatus[] = [
  "Draft",
  "Requested",
  "Confirmed",
  "Dispatched",
  "PartiallyReceived",
  "Received",
  "Completed",
  "Rejected",
  "Cancelled",
];
export function SubcontractingPage({ section = "overview" }: { section?: SubcontractingSection }) {
  const { user } = useAuth();
  const { t, formatNumber, locale } = useLocale();
  const orderProtection=useIntegrationProtection("ExternalProductionOrder");
  const supplierProtection=useIntegrationProtection("ProductionSupplier");
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<SubcontractingSnapshot | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);
  const pageSize = getPreferredPageSize(params);
  const appliedQuery = params.get("q") ?? "";
  const appliedStatus = params.get("status") ?? "";
  const [query, setQuery] = useState(appliedQuery);
  const [status, setStatus] = useState(appliedStatus);
  useEffect(() => { setQuery(appliedQuery); setStatus(appliedStatus); }, [appliedQuery, appliedStatus]);
  const edit = params.get("edit"),
    isNew = params.get("new") === "1";
  const load = async () => {
    try {
      setData(await getSubcontracting());
      setError("");
    } catch {
      setError("subcontracting.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("edit");
    next.delete("new");
    setParams(next, { replace: true });
  };
  const openNew = () => {
    const next = new URLSearchParams(params);
    next.delete("edit");
    next.set("new", "1");
    setParams(next);
  };
  const canCreate=section==="orders"?orderProtection.canCreate:supplierProtection.canCreate;
  const showNew=isNew&&canCreate;
  const save = async (path: string, method: "POST" | "PUT", body: unknown) => {
    setBusy(true);
    try {
      const result = await subcontractingCommand(path, method, body);
      await load();
      if (method === "POST" && result?.id) {
        const next = new URLSearchParams(params);
        next.delete("new");
        next.set("edit", result.id);
        setParams(next, { replace: true });
      } else {
        close();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "subcontracting.actionError");
    } finally {
      setBusy(false);
    }
  };
  const saveRelated = async (path: string, method: "POST" | "PUT", body: unknown) => {
    setBusy(true);
    try { await subcontractingCommand(path, method, body); await load(); return true; }
    catch (e) { setError(e instanceof Error ? e.message : "subcontracting.actionError"); return false; }
    finally { setBusy(false); }
  };
  const allOrders: Array<ExternalOrder|ExternalOrderHeader> = data?[...data.orderHeaders,...data.orders.filter(order=>!data.orderHeaders.some(header=>header.number===order.number))]:[];
  const orderSort=useUrlSortedRows(allOrders,{code:x=>x.number,production:x=>"productionOrderId"in x?data?.productionOrders.find(o=>o.id===x.productionOrderId)?.number:null,supplier:x=>data?.suppliers.find(s=>s.id===x.supplierId)?.code,quantity:x=>"orderedQuantity"in x?x.orderedQuantity:data?.orderLines.filter(line=>line.externalProductionOrderId===x.id).reduce((sum,line)=>sum+line.orderedQuantity,0),status:x=>x.status},'code','asc',locale);
  const supplierSort=useUrlSortedRows(data?.suppliers??[],{code:x=>x.code,name:x=>x.name,lead:x=>x.defaultLeadTimeMinutes,status:x=>x.isActive},'code','asc',locale);
  if (!data)
    return (
      <PageLoading
        label={t("subcontracting.loading")}
        error={error ? t(error as Parameters<typeof t>[0]) : null}
      />
    );
  const combinedOrders = allOrders;
  const item =
    section === "overview" ? undefined : section === "orders"
      ? combinedOrders.find((x) => x.id === edit)
      : data.suppliers.find((x) => x.id === edit);
  const records = section === "overview" ? [] : section === "orders" ? orderSort.sortedRows : supplierSort.sortedRows;
  const normalizedQuery = appliedQuery.trim().toLocaleLowerCase();
  const filteredRecords = records.filter((record) => {
    const searchable = section === "orders"
      ? `${(record as ExternalOrder | ExternalOrderHeader).number} ${"productionOrderId" in record ? data.productionOrders.find(order => order.id === record.productionOrderId)?.number ?? "" : ""} ${data.suppliers.find(supplier => supplier.id === (record as ExternalOrder | ExternalOrderHeader).supplierId)?.name ?? ""}`
      : `${(record as Supplier).code} ${(record as Supplier).name} ${(record as Supplier).description}`;
    const matchesQuery = !normalizedQuery || searchable.toLocaleLowerCase().includes(normalizedQuery);
    const matchesStatus = !appliedStatus || (section === "orders"
      ? (record as ExternalOrder).status === appliedStatus
      : (record as Supplier).isActive === (appliedStatus === "active"));
    return matchesQuery && matchesStatus;
  });
  const pageCount = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const visibleRecords = filteredRecords.slice((safePage - 1) * pageSize, safePage * pageSize);
  const recordIndex = item ? filteredRecords.findIndex((record) => record.id === item.id) : -1;
  const openRecord = (index: number) => {
    const record = filteredRecords[index];
    if (record) {
      const next = new URLSearchParams(params);
      next.delete("new");
      next.set("edit", record.id);
      setParams(next);
    }
  };
  const detailTitle = section === "orders"
    ? item ? (item as ExternalOrder | ExternalOrderHeader).number : t("subcontracting.newOrder")
    : item ? `${(item as Supplier).code} · ${(item as Supplier).name}` : t("subcontracting.newSupplier");
  return (
    <main className="page-content subcontracting-page">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t("subcontracting.eyebrow")}</span>
          <h1>{t(section === "overview" ? "subcontracting.title" : `subcontracting.${section}`)}</h1>
          <p>
            {data.companyName} · {t("subcontracting.subtitle")}
          </p>
        </div>
      </header>
      {error && (
        <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>
      )}
      {section === "overview" && <section className="capacity-overview-grid">
        {([
          { key: "orders", route: "/app/subcontratacion/ordenes", count: combinedOrders.length },
          { key: "suppliers", route: "/app/subcontratacion/proveedores", count: data.suppliers.length },
        ] as const).map(card => <Link className="capacity-overview-card" to={card.route} key={card.key}>
          <span className="capacity-overview-count">{formatNumber(card.count)}</span>
          <h2>{t(`subcontracting.${card.key}`)}</h2>
          <p>{t(`subcontracting.${card.key}Description`)}</p>
          <span className="capacity-overview-link">{t("capacity.openSection")} →</span>
        </Link>)}
      </section>}
      {(item || showNew) && (
        <AppModal key={item?.id ?? `new-${section}`} title={detailTitle}
          closeLabel={t("recordNavigation.backToList")} onClose={close} wide
          saveForm={user?.permissions.subcontracting === "Write"} saveDisabled={busy}
          onPrevious={!showNew && recordIndex > 0 ? () => openRecord(recordIndex - 1) : null}
          onNext={!showNew && recordIndex >= 0 && recordIndex < filteredRecords.length - 1 ? () => openRecord(recordIndex + 1) : null}
          previousLabel={t("recordNavigation.previous")} nextLabel={t("recordNavigation.next")}
          feedback={error ? t(error as Parameters<typeof t>[0]) : null}>
          {section === "suppliers" ? (
            <SupplierForm
              key={item?.id ?? "new-supplier"}
              data={data}
              item={item as Supplier | undefined}
              busy={busy}
              save={(body) =>
                save(
                  `/suppliers${item ? `/${item.id}` : ""}`,
                  item ? "PUT" : "POST",
                  body,
                )
              }
              saveRelated={saveRelated}
            />
          ) : item && "destinationCenterCode" in item ? (
            <OrderHeaderForm key={item.id} data={data} item={item as ExternalOrderHeader}
              busy={busy}
              save={(body) => save(`/order-headers/${item.id}`, "PUT", body)}
              saveRelated={saveRelated} />
          ) : showNew && section === "orders" ? (
            <OrderHeaderForm key="new-order-header" data={data} busy={busy}
              save={(body) => save("/order-headers", "POST", body)}
              saveRelated={saveRelated} />
          ) : (
            <OrderForm
              key={item?.id ?? "new-order"}
              data={data}
              item={item as ExternalOrder | undefined}
              busy={busy}
              save={(body) =>
                save(
                  `/orders${item ? `/${item.id}` : ""}`,
                  item ? "PUT" : "POST",
                  body,
                )
              }
              status={(statusBody) =>
                item && save(`/orders/${item.id}/status`, "PUT", statusBody)
              }
            />
          )}
        </AppModal>
      )}
      {section !== "overview" && !item && !showNew && (
        <section className="data-panel administration-panel">
          <div className="section-heading"><h2>
            {t(
              section === "orders"
                ? "subcontracting.orders"
                : "subcontracting.suppliers",
            )}
          </h2>{user?.permissions.subcontracting === "Write" && canCreate && <button
            className="primary-button" type="button" onClick={openNew}>
            + {t(section === "orders" ? "subcontracting.newOrder" : "subcontracting.newSupplier")}
          </button>}</div>
          <PagedListControls applyLabel={t("recordList.applyFilters")} clearLabel={t("recordList.clearFilters")}
            rowsPerPageLabel={t("recordList.rowsPerPage")} previousLabel={t("recordList.previousPage")}
            nextLabel={t("recordList.nextPage")} pageLabel={t("recordList.pageOf")}
            totalLabel={t("recordList.totalRecords")} page={safePage} pageSize={pageSize}
            totalCount={filteredRecords.length} formatNumber={formatNumber}
            filters={<><label><span>{t("nav.search")}</span><input value={query}
              placeholder={t("recordList.search")} onChange={event => setQuery(event.target.value)} /></label>
              <label><span>{t("administration.status")}</span><select value={status} onChange={event => setStatus(event.target.value)}>
                <option value="">{t("recordList.allStatuses")}</option>
                {section === "orders" ? statuses.map(value => <option value={value} key={value}>{t(`subcontracting.status.${value}`)}</option>) : <>
                  <option value="active">{t("administration.active")}</option><option value="inactive">{t("administration.inactive")}</option></>}
              </select></label></>}
            onApply={() => setParams(listParams(params,{q:query.trim()||undefined,status:status||undefined,page:"1",pageSize:String(pageSize)}))}
            onClear={() => { setQuery(""); setStatus(""); setParams(listParams(params,{page:"1",pageSize:String(pageSize)})); }}
            onPageChange={next => setParams(listParams(params,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:String(next),pageSize:String(pageSize)}))}
            onPageSizeChange={next => { rememberPageSize(next); setParams(listParams(params,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:"1",pageSize:String(next)})); }} />
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  {section === "orders" ? (
                    <SortableHeaders sort={orderSort} columns={[["code",t("subcontracting.number")],["production",t("subcontracting.productionOrder")],["supplier",t("subcontracting.supplier")],["quantity",t("materials.quantity")],["status",t("administration.status")]]}/>
                  ) : (
                    <SortableHeaders sort={supplierSort} columns={[["code",t("aplPanel.code")],["name",t("aplPanel.name")],["lead",t("subcontracting.defaultLeadTime")],["status",t("administration.status")]]}/>
                  )}
                </tr>
              </thead>
              <tbody>
                {section === "orders"
                  ? (visibleRecords as (ExternalOrder | ExternalOrderHeader)[]).map((x) => {
                      const lines = "destinationCenterCode" in x ? data.orderLines.filter(line => line.externalProductionOrderId === x.id) : [];
                      const productionNumber = "productionOrderId" in x ? data.productionOrders.find(o => o.id === x.productionOrderId)?.number : lines.map(line => data.productionOrders.find(o => o.id === line.productionOrderId)?.number).filter(Boolean).join(", ");
                      const quantity = "orderedQuantity" in x ? x.orderedQuantity : lines.reduce((sum, line) => sum + line.orderedQuantity, 0);
                      return (
                      <tr key={x.id}>
                        <td data-label={t("subcontracting.number")}>
                          <button
                            className="record-code-link button-link"
                            onClick={() => openRecord(filteredRecords.findIndex(record => record.id === x.id))}
                          >
                            {x.number}
                          </button>
                        </td>
                        <td data-label={t("subcontracting.productionOrder")}>
                          {
                            productionNumber
                          }
                        </td>
                        <td data-label={t("subcontracting.supplier")}>
                          {
                            data.suppliers.find((s) => s.id === x.supplierId)
                              ?.name
                          }
                        </td>
                        <td data-label={t("materials.quantity")}>{formatNumber(quantity)}</td>
                        <td data-label={t("administration.status")}>
                          {t(
                            `subcontracting.status.${x.status}` as Parameters<
                              typeof t
                            >[0],
                          )}
                        </td>
                      </tr>
                    )})
                  : (visibleRecords as Supplier[]).map((x) => (
                        <tr key={x.id}>
                          <td data-label={t("aplPanel.code")}>
                            <button
                              className="record-code-link button-link"
                              onClick={() => openRecord(filteredRecords.findIndex(record => record.id === x.id))}
                            >
                              {x.code}
                            </button>
                          </td>
                          <td data-label={t("aplPanel.name")}>{x.name}</td>
                          <td data-label={t("subcontracting.defaultLeadTime")}>{formatNumber(x.defaultLeadTimeMinutes)} min</td>
                          <td data-label={t("administration.status")}>
                            {t(
                              x.isActive
                                ? "administration.active"
                                : "administration.inactive",
                            )}
                          </td>
                        </tr>
                      ))}
              </tbody>
            </table>
          </div>
          {visibleRecords.length === 0 && <p className="empty-state">{t("administration.empty")}</p>}
        </section>
      )}
    </main>
  );
}
function OrderHeaderForm({ data, item, save, saveRelated }: { data:SubcontractingSnapshot; item?:ExternalOrderHeader; busy:boolean; save:(x:unknown)=>void; saveRelated:(path:string,method:"POST"|"PUT",body:unknown)=>Promise<boolean> }) {
  const { t, formatNumber, formatDate } = useLocale();
  const lineProtection=useIntegrationProtection("ExternalProductionOrderLine");
  const [editingLine,setEditingLine]=useState<ExternalOrderLine|null>(null);
  const [addingLine,setAddingLine]=useState(false);
  const [linePage,setLinePage]=useState(1);
  const [tab,setTab]=useState<"details"|"lines">("details");
  const relatedPanelRef=useRef<HTMLDivElement>(null);
  const [f,setF]=useState({number:item?.number??"",supplierId:item?.supplierId??"",supplierWarehouseId:item?.supplierWarehouseId??"",destinationCenterCode:item?.destinationCenterCode??"",createdAtSourceUtc:item?.createdAtSourceUtc?.slice(0,16)??"",startedAtUtc:item?.startedAtUtc?.slice(0,16)??"",closedAtUtc:item?.closedAtUtc?.slice(0,16)??"",sourceStatus:item?.sourceStatus??"",origin:item?.origin??"",accountingCode:item?.accountingCode??"",currencyCode:item?.currencyCode??"EUR",exchangeRate:item?.exchangeRate??1,paymentMethod:item?.paymentMethod??"",incoterm:item?.incoterm??"",shippingMethod:item?.shippingMethod??"",transportMode:item?.transportMode??"",notes:item?.notes??"",status:item?.status??"Draft"});
  const dt=(v:string)=>(v?new Date(v).toISOString():null);
  const lines=item?data.orderLines.filter(x=>x.externalProductionOrderId===item.id):[];
  const safeLinePage=Math.min(linePage,Math.max(1,Math.ceil(lines.length/10)));
  const visibleLines=lines.slice((safeLinePage-1)*10,safeLinePage*10);
  useEffect(()=>{setLinePage(1);setTab("details");setAddingLine(false);setEditingLine(null)},[item?.id]);
  useEffect(()=>{
    if(!addingLine&&!editingLine)return;
    const frame=requestAnimationFrame(()=>relatedPanelRef.current?.scrollIntoView({block:"start",behavior:"smooth"}));
    return()=>cancelAnimationFrame(frame);
  },[addingLine,editingLine]);
  return <>
    <DetailTabs value={tab} onChange={setTab} relatedValue="lines" relatedDisabled={!item}
      relatedCount={lines.length} relatedLabel={t("subcontracting.lines")}/>
    <div role="tabpanel" hidden={tab!=="details"}>
    <form id="subcontracting-editor" className="administration-form" onSubmit={e=>{e.preventDefault();save({...f,number:item?null:f.number,supplierWarehouseId:f.supplierWarehouseId||null,createdAtSourceUtc:dt(f.createdAtSourceUtc),startedAtUtc:dt(f.startedAtUtc),closedAtUtc:dt(f.closedAtUtc)})}}>
      <h2>{t(item?"subcontracting.editOrder":"subcontracting.newOrder")}</h2>
      <IntegrationProtectionBoundary entity="ExternalProductionOrder" fields={['number','supplierCode','supplierWarehouseCode','destinationCenterCode','createdAtSourceUtc','startedAtUtc','closedAtUtc','sourceStatus','origin','accountingCode','currencyCode','exchangeRate','paymentMethod','incoterm','shippingMethod','transportMode','notes','status']}><div className="compact-form-grid">
        <F l={t("subcontracting.number")}><input required disabled={!!item} value={f.number} onChange={e=>setF({...f,number:e.target.value})}/></F>
        <F l={t("subcontracting.supplier")}><EntityPicker label={t("subcontracting.supplier")} value={f.supplierId} options={data.suppliers.filter(x=>x.isActive)} onChange={supplierId=>setF({...f,supplierId})}/></F>
        <F l={t("subcontracting.supplierWarehouse")}><select value={f.supplierWarehouseId} onChange={e=>setF({...f,supplierWarehouseId:e.target.value})}><option value="">—</option>{data.warehouses.map(x=><option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}</select></F>
        <F l={t("subcontracting.destinationCenter")}><input value={f.destinationCenterCode} onChange={e=>setF({...f,destinationCenterCode:e.target.value})}/></F>
        <F l={t("subcontracting.sourceStatus")}><input value={f.sourceStatus} onChange={e=>setF({...f,sourceStatus:e.target.value})}/></F>
        <F l={t("subcontracting.origin")}><input value={f.origin} onChange={e=>setF({...f,origin:e.target.value})}/></F>
        <F l={t("subcontracting.accountingCode")}><input value={f.accountingCode} onChange={e=>setF({...f,accountingCode:e.target.value})}/></F>
        <F l={t("subcontracting.currency")}><input value={f.currencyCode} onChange={e=>setF({...f,currencyCode:e.target.value})}/></F>
        <N l={t("subcontracting.exchangeRate")} v={f.exchangeRate} s={exchangeRate=>setF({...f,exchangeRate})}/>
        <F l={t("subcontracting.paymentMethod")}><input value={f.paymentMethod} onChange={e=>setF({...f,paymentMethod:e.target.value})}/></F>
        <F l={t("subcontracting.incoterm")}><input value={f.incoterm} onChange={e=>setF({...f,incoterm:e.target.value})}/></F>
        <F l={t("subcontracting.shippingMethod")}><input value={f.shippingMethod} onChange={e=>setF({...f,shippingMethod:e.target.value})}/></F>
        <F l={t("subcontracting.transportMode")}><input value={f.transportMode} onChange={e=>setF({...f,transportMode:e.target.value})}/></F>
        <F l={t("subcontracting.createdAtSource")}><input type="datetime-local" value={f.createdAtSourceUtc} onChange={e=>setF({...f,createdAtSourceUtc:e.target.value})}/></F>
        <F l={t("subcontracting.startedAt")}><input type="datetime-local" value={f.startedAtUtc} onChange={e=>setF({...f,startedAtUtc:e.target.value})}/></F>
        <F l={t("subcontracting.closedAt")}><input type="datetime-local" value={f.closedAtUtc} onChange={e=>setF({...f,closedAtUtc:e.target.value})}/></F>
        <F l={t("administration.status")}><select value={f.status} onChange={e=>setF({...f,status:e.target.value as ExternalOrderStatus})}>{statuses.map(x=><option key={x} value={x}>{t(`subcontracting.status.${x}`)}</option>)}</select></F>
        <F l={t("subcontracting.notes")}><textarea value={f.notes} onChange={e=>setF({...f,notes:e.target.value})}/></F>
      </div></IntegrationProtectionBoundary>
    </form>
    </div>
    <div ref={relatedPanelRef} role="tabpanel" hidden={tab!=="lines"}>
    {item&&<RelatedRecordsPanel title={t("subcontracting.lines")} action={lineProtection.canCreate?<button type="button" className="primary-button" onClick={()=>{setEditingLine(null);setAddingLine(true)}}>+ {t("subcontracting.newLine")}</button>:null}>
      {(addingLine||editingLine)&&<OrderLineEditor data={data} headerId={item.id} item={editingLine??undefined} close={()=>{setAddingLine(false);setEditingLine(null)}} save={body=>saveRelated(`/order-lines${editingLine?`/${editingLine.id}`:""}`,editingLine?"PUT":"POST",body)}/>}
      <div className="administration-table-wrap"><table className="data-table administration-table"><thead><tr><th>{t("subcontracting.lineNumber")}</th><th>{t("materials.article")}</th><th>{t("subcontracting.productionOrder")}</th><th>{t("materials.quantity")}</th><th>{t("subcontracting.requiredAt")}</th></tr></thead><tbody>{lines.length===0&&<EmptyRelatedRow columns={5}/>} {visibleLines.map(line=><tr key={line.id}><td data-label={t("subcontracting.lineNumber")}><button type="button" className="record-code-link button-link" onClick={()=>{setEditingLine(line);setAddingLine(false)}}>{line.lineNumber}</button></td><td data-label={t("materials.article")}><strong>{line.articleCode}</strong>{line.articleName?` · ${line.articleName}`:""}</td><td data-label={t("subcontracting.productionOrder")}>{data.productionOrders.find(x=>x.id===line.productionOrderId)?.number??"—"}</td><td data-label={t("materials.quantity")}>{formatNumber(line.orderedQuantity)}</td><td data-label={t("subcontracting.requiredAt")}>{formatDate(line.requiredAtUtc)}</td></tr>)}</tbody></table></div>
      <RelatedPager page={safeLinePage} total={lines.length} setPage={setLinePage}/>
    </RelatedRecordsPanel>}
    {!item&&<section className="related-records-section"><h3>{t("subcontracting.lines")}</h3><p className="muted-text">{t("subcontracting.saveOrderBeforeLines")}</p></section>}
    </div>
  </>;
}
function SupplierForm({
  data,
  item,
  save,
  saveRelated,
}: {
  data: SubcontractingSnapshot;
  item?: Supplier;
  busy: boolean;
  save: (x: unknown) => void;
  saveRelated: (path:string,method:"POST"|"PUT",body:unknown)=>Promise<boolean>;
}) {
  const { t } = useLocale();
  const relationProtection=useIntegrationProtection("ExternalProductionSupplierArticle");
  const [editingArticle,setEditingArticle]=useState<SupplierArticle|null>(null);
  const [addingArticle,setAddingArticle]=useState(false);
  const [articlePage,setArticlePage]=useState(1);
  const [tab,setTab]=useState<"details"|"related">("details");
  const relatedPanelRef=useRef<HTMLDivElement>(null);
  const [f, setF] = useState({
    code: item?.code ?? "",
    name: item?.name ?? "",
    description: item?.description ?? "",
    externalReference: item?.externalReference ?? "",
    outboundTransportMinutes: item?.outboundTransportMinutes ?? 0,
    returnTransportMinutes: item?.returnTransportMinutes ?? 0,
    defaultLeadTimeMinutes: item?.defaultLeadTimeMinutes ?? 0,
    fixedCost: item?.fixedCost ?? 0,
    unitCost: item?.unitCost ?? 0,
    isActive: item?.isActive ?? true,
  });
  const relations=item?data.supplierArticles.filter(x=>x.supplierId===item.id):[];
  const safeArticlePage=Math.min(articlePage,Math.max(1,Math.ceil(relations.length/10)));
  const visibleRelations=relations.slice((safeArticlePage-1)*10,safeArticlePage*10);
  useEffect(()=>{setArticlePage(1);setTab("details");setAddingArticle(false);setEditingArticle(null)},[item?.id]);
  useEffect(()=>{
    if(!addingArticle&&!editingArticle)return;
    const frame=requestAnimationFrame(()=>relatedPanelRef.current?.scrollIntoView({block:"start",behavior:"smooth"}));
    return()=>cancelAnimationFrame(frame);
  },[addingArticle,editingArticle]);
  return (<>
    <DetailTabs value={tab} onChange={setTab} relatedValue="related" relatedDisabled={!item}
      relatedCount={relations.length} relatedLabel={t("subcontracting.articles")}/>
    <div role="tabpanel" hidden={tab!=="details"}>
    <form
      id="subcontracting-editor"
      className="administration-form"
      onSubmit={(e) => {
        e.preventDefault();
        save({ ...f, code: item ? null : f.code });
      }}
    >
      <h2>
        {t(item ? "subcontracting.editSupplier" : "subcontracting.newSupplier")}
      </h2>
      <IntegrationProtectionBoundary entity="ProductionSupplier" fields={['code','name','externalReference','outboundTransportMinutes','returnTransportMinutes','defaultLeadTimeMinutes','fixedCost','unitCost','description','isActive']}>
      <div className="compact-form-grid">
        <F l={t("aplPanel.code")}>
          <input
            required
            disabled={!!item}
            value={f.code}
            onChange={(e) => setF({ ...f, code: e.target.value })}
          />
        </F>
        <F l={t("aplPanel.name")}>
          <input
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </F>
        <F l={t("subcontracting.externalReference")}>
          <input
            value={f.externalReference}
            onChange={(e) => setF({ ...f, externalReference: e.target.value })}
          />
        </F>
        <N
          l={t("subcontracting.outboundTransport")}
          v={f.outboundTransportMinutes}
          s={(v) => setF({ ...f, outboundTransportMinutes: v })}
        />
        <N
          l={t("subcontracting.returnTransport")}
          v={f.returnTransportMinutes}
          s={(v) => setF({ ...f, returnTransportMinutes: v })}
        />
        <N
          l={t("subcontracting.defaultLeadTime")}
          v={f.defaultLeadTimeMinutes}
          s={(v) => setF({ ...f, defaultLeadTimeMinutes: v })}
        />
        <N
          l={t("subcontracting.fixedCost")}
          v={f.fixedCost}
          s={(v) => setF({ ...f, fixedCost: v })}
        />
        <N
          l={t("subcontracting.unitCost")}
          v={f.unitCost}
          s={(v) => setF({ ...f, unitCost: v })}
        />
      </div>
      <F l={t("capacity.description")}>
        <textarea
          value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })}
        />
      </F>
      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={f.isActive}
          onChange={(e) => setF({ ...f, isActive: e.target.checked })}
        />
        <span>{t("administration.active")}</span>
      </label>
      </IntegrationProtectionBoundary>
    </form>
    </div>
    <div ref={relatedPanelRef} role="tabpanel" hidden={tab!=="related"}>
    {item&&<RelatedRecordsPanel title={t("subcontracting.articles")} action={relationProtection.canCreate?<button type="button" className="primary-button" onClick={()=>{setEditingArticle(null);setAddingArticle(true)}}>+ {t("subcontracting.newArticle")}</button>:null}>
      {(addingArticle||editingArticle)&&<SupplierArticleEditor data={data} supplierId={item.id} item={editingArticle??undefined} close={()=>{setAddingArticle(false);setEditingArticle(null)}} save={body=>saveRelated(`/supplier-articles${editingArticle?`/${editingArticle.id}`:""}`,editingArticle?"PUT":"POST",body)}/>}
      <div className="administration-table-wrap"><table className="data-table administration-table"><thead><tr><th>{t("materials.article")}</th><th>{t("subcontracting.quantityMultiple")}</th><th>{t("subcontracting.optimalQuantity")}</th><th>{t("subcontracting.unitCost")}</th><th>{t("administration.status")}</th></tr></thead><tbody>{relations.length===0&&<EmptyRelatedRow columns={5}/>} {visibleRelations.map(x=><tr key={x.id}><td data-label={t("materials.article")}><button type="button" className="record-code-link button-link" onClick={()=>{setEditingArticle(x);setAddingArticle(false)}}>{x.articleCode}</button>{x.articleName&&<small className="record-cell-secondary">{x.articleName}</small>}</td><td data-label={t("subcontracting.quantityMultiple")}>{x.quantityMultiple}</td><td data-label={t("subcontracting.optimalQuantity")}>{x.optimalQuantity}</td><td data-label={t("subcontracting.unitCost")}>{x.unitCost}</td><td data-label={t("administration.status")}>{t(x.isActive?"administration.active":"administration.inactive")}</td></tr>)}</tbody></table></div>
      <RelatedPager page={safeArticlePage} total={relations.length} setPage={setArticlePage}/>
    </RelatedRecordsPanel>}
    {!item&&<section className="related-records-section"><h3>{t("subcontracting.articles")}</h3><p className="muted-text">{t("subcontracting.saveSupplierBeforeArticles")}</p></section>}
    </div>
  </>);
}
function OrderForm({
  data,
  item,
  busy,
  save,
  status,
}: {
  data: SubcontractingSnapshot;
  item?: ExternalOrder;
  busy: boolean;
  save: (x: unknown) => void;
  status: (x: unknown) => void;
}) {
  const { t } = useLocale();
  const [f, setF] = useState({
    number: item?.number ?? "",
    productionOrderId: item?.productionOrderId ?? "",
    productionOrderOperationId: item?.productionOrderOperationId ?? "",
    supplierId: item?.supplierId ?? "",
    orderedQuantity: item?.orderedQuantity ?? 1,
    requiredAtUtc: (item?.requiredAtUtc ?? new Date().toISOString()).slice(
      0,
      16,
    ),
    plannedDispatchAtUtc: item?.plannedDispatchAtUtc?.slice(0, 16) ?? "",
    confirmedDispatchAtUtc: item?.confirmedDispatchAtUtc?.slice(0, 16) ?? "",
    plannedReceiptAtUtc: item?.plannedReceiptAtUtc?.slice(0, 16) ?? "",
    confirmedReceiptAtUtc: item?.confirmedReceiptAtUtc?.slice(0, 16) ?? "",
    outboundWarehouseId: item?.outboundWarehouseId ?? "",
    supplierWarehouseId: item?.supplierWarehouseId ?? "",
    returnWarehouseId: item?.returnWarehouseId ?? "",
    plannedCost: item?.plannedCost ?? 0,
    actualCost: item?.actualCost ?? 0,
    externalReference: item?.externalReference ?? "",
    notes: item?.notes ?? "",
  });
  const [st, setSt] = useState({
    status: item?.status ?? "Draft",
    dispatchedQuantity: item?.dispatchedQuantity ?? 0,
    receivedQuantity: item?.receivedQuantity ?? 0,
    acceptedQuantity: item?.acceptedQuantity ?? 0,
    rejectedQuantity: item?.rejectedQuantity ?? 0,
    actualDispatchAtUtc: item?.actualDispatchAtUtc?.slice(0, 16) ?? "",
    actualReceiptAtUtc: item?.actualReceiptAtUtc?.slice(0, 16) ?? "",
  });
  const ops = data.operations.filter(
    (x) => x.productionOrderId === f.productionOrderId,
  );
  const dt = (v: string) => (v ? new Date(v).toISOString() : null);
  return (
    <>
      <form
        id="subcontracting-editor"
        className="administration-form"
        onSubmit={(e) => {
          e.preventDefault();
          save({
            ...f,
            number: item ? null : f.number,
            requiredAtUtc: dt(f.requiredAtUtc),
            plannedDispatchAtUtc: dt(f.plannedDispatchAtUtc),
            confirmedDispatchAtUtc: dt(f.confirmedDispatchAtUtc),
            plannedReceiptAtUtc: dt(f.plannedReceiptAtUtc),
            confirmedReceiptAtUtc: dt(f.confirmedReceiptAtUtc),
            outboundWarehouseId: f.outboundWarehouseId || null,
            supplierWarehouseId: f.supplierWarehouseId || null,
            returnWarehouseId: f.returnWarehouseId || null,
          });
        }}
      >
        <h2>
          {t(item ? "subcontracting.editOrder" : "subcontracting.newOrder")}
        </h2>
        <div className="compact-form-grid">
          <F l={t("subcontracting.number")}>
            <input
              required
              disabled={!!item}
              value={f.number}
              onChange={(e) => setF({ ...f, number: e.target.value })}
            />
          </F>
          <F l={t("subcontracting.productionOrder")}>
            <EntityPicker
              disabled={!!item}
              label={t("subcontracting.productionOrder")}
              value={f.productionOrderId}
              options={data.productionOrders.map((x) => ({
                id: x.id,
                code: x.number,
                name: `${x.articleCode} · ${x.articleName}`,
              }))}
              onChange={(productionOrderId) =>
                setF({
                  ...f,
                  productionOrderId,
                  productionOrderOperationId: "",
                })
              }
            />
          </F>
          <F l={t("subcontracting.operation")}>
            <EntityPicker
              disabled={!!item}
              label={t("subcontracting.operation")}
              value={f.productionOrderOperationId}
              options={ops.map((x) => ({
                id: x.id,
                code: `${x.sequence} · ${x.code}`,
                name: x.name,
              }))}
              onChange={(productionOrderOperationId) =>
                setF({ ...f, productionOrderOperationId })
              }
            />
          </F>
          <F l={t("subcontracting.supplier")}>
            <EntityPicker
              label={t("subcontracting.supplier")}
              value={f.supplierId}
              options={data.suppliers.filter((x) => x.isActive)}
              onChange={(supplierId) => setF({ ...f, supplierId })}
            />
          </F>
          <N
            l={t("materials.quantity")}
            v={f.orderedQuantity}
            s={(v) => setF({ ...f, orderedQuantity: v })}
          />
          <F l={t("subcontracting.requiredAt")}>
            <input
              type="datetime-local"
              required
              value={f.requiredAtUtc}
              onChange={(e) => setF({ ...f, requiredAtUtc: e.target.value })}
            />
          </F>
          <F l={t("subcontracting.plannedDispatch")}>
            <input
              type="datetime-local"
              value={f.plannedDispatchAtUtc}
              onChange={(e) =>
                setF({ ...f, plannedDispatchAtUtc: e.target.value })
              }
            />
          </F>
          <F l={t("subcontracting.confirmedDispatch")}>
            <input
              type="datetime-local"
              value={f.confirmedDispatchAtUtc}
              onChange={(e) =>
                setF({ ...f, confirmedDispatchAtUtc: e.target.value })
              }
            />
          </F>
          <F l={t("subcontracting.plannedReceipt")}>
            <input
              type="datetime-local"
              value={f.plannedReceiptAtUtc}
              onChange={(e) =>
                setF({ ...f, plannedReceiptAtUtc: e.target.value })
              }
            />
          </F>
          <F l={t("subcontracting.confirmedReceipt")}>
            <input
              type="datetime-local"
              value={f.confirmedReceiptAtUtc}
              onChange={(e) =>
                setF({ ...f, confirmedReceiptAtUtc: e.target.value })
              }
            />
          </F>
          <F l={t("subcontracting.outboundWarehouse")}>
            <select value={f.outboundWarehouseId} onChange={(e) => setF({ ...f, outboundWarehouseId: e.target.value })}>
              <option value="">—</option>{data.warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>)}
            </select>
          </F>
          <F l={t("subcontracting.supplierWarehouse")}>
            <select value={f.supplierWarehouseId} onChange={(e) => setF({ ...f, supplierWarehouseId: e.target.value })}>
              <option value="">—</option>{data.warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>)}
            </select>
          </F>
          <F l={t("subcontracting.returnWarehouse")}>
            <select value={f.returnWarehouseId} onChange={(e) => setF({ ...f, returnWarehouseId: e.target.value })}>
              <option value="">—</option>{data.warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>)}
            </select>
          </F>
          <N
            l={t("subcontracting.plannedCost")}
            v={f.plannedCost}
            s={(v) => setF({ ...f, plannedCost: v })}
          />
          <N
            l={t("subcontracting.actualCost")}
            v={f.actualCost}
            s={(v) => setF({ ...f, actualCost: v })}
          />
        </div>
        <F l={t("subcontracting.externalReference")}>
          <input
            value={f.externalReference}
            onChange={(e) => setF({ ...f, externalReference: e.target.value })}
          />
        </F>
        <F l={t("orders.notes")}>
          <textarea
            value={f.notes}
            onChange={(e) => setF({ ...f, notes: e.target.value })}
          />
        </F>
      </form>
      {item && (
        <form
          className="administration-form"
          onSubmit={(e) => {
            e.preventDefault();
            status({
              ...st,
              actualDispatchAtUtc: dt(st.actualDispatchAtUtc),
              actualReceiptAtUtc: dt(st.actualReceiptAtUtc),
            });
          }}
        >
          <h2>{t("subcontracting.execution")}</h2>
          <div className="compact-form-grid">
            <F l={t("administration.status")}>
              <select
                value={st.status}
                onChange={(e) =>
                  setSt({
                    ...st,
                    status: e.target.value as ExternalOrderStatus,
                  })
                }
              >
                {statuses.map((x) => (
                  <option key={x} value={x}>
                    {t(`subcontracting.status.${x}` as Parameters<typeof t>[0])}
                  </option>
                ))}
              </select>
            </F>
            <N
              l={t("subcontracting.dispatchedQuantity")}
              v={st.dispatchedQuantity}
              s={(v) => setSt({ ...st, dispatchedQuantity: v })}
            />
            <N
              l={t("subcontracting.receivedQuantity")}
              v={st.receivedQuantity}
              s={(v) => setSt({ ...st, receivedQuantity: v })}
            />
            <N
              l={t("subcontracting.acceptedQuantity")}
              v={st.acceptedQuantity}
              s={(v) => setSt({ ...st, acceptedQuantity: v })}
            />
            <N
              l={t("subcontracting.rejectedQuantity")}
              v={st.rejectedQuantity}
              s={(v) => setSt({ ...st, rejectedQuantity: v })}
            />
            <F l={t("subcontracting.actualDispatch")}>
              <input type="datetime-local" value={st.actualDispatchAtUtc} onChange={(e) => setSt({ ...st, actualDispatchAtUtc: e.target.value })} />
            </F>
            <F l={t("subcontracting.actualReceipt")}>
              <input type="datetime-local" value={st.actualReceiptAtUtc} onChange={(e) => setSt({ ...st, actualReceiptAtUtc: e.target.value })} />
            </F>
          </div>
          <button className="primary-button" disabled={busy}>
            {t("subcontracting.updateStatus")}
          </button>
        </form>
      )}
    </>
  );
}
function SupplierArticleEditor({data,supplierId,item,save,close}:{data:SubcontractingSnapshot;supplierId:string;item?:SupplierArticle;save:(x:unknown)=>Promise<boolean>;close:()=>void}){
  const {t}=useLocale();
  const [f,setF]=useState({supplierId,articleId:item?.articleId??"",deliveryControl:item?.deliveryControl??false,quantityMultiple:item?.quantityMultiple??0,optimalQuantity:item?.optimalQuantity??0,unitCost:item?.unitCost??0,leadTimeDays:item?.leadTimeDays??0,unitOfMeasureId:item?.unitOfMeasureId??"",isActive:item?.isActive??true});
  return <form className="administration-form related-record-editor inventory-inline-form" onSubmit={async e=>{e.preventDefault();if(await save({...f,unitOfMeasureId:f.unitOfMeasureId||null}))close()}}>
    <div className="section-heading"><h3>{t(item?"subcontracting.editArticle":"subcontracting.newArticle")}</h3><button type="button" className="secondary-button" onClick={close}>{t("administration.cancel")}</button></div>
    <IntegrationProtectionBoundary entity="ExternalProductionSupplierArticle" fields={['articleCode','unitCode','quantityMultiple','optimalQuantity','unitCost','leadTimeDays','deliveryControl','isActive']}><div className="compact-form-grid">
      <F l={t("materials.article")}><EntityPicker label={t("materials.article")} value={f.articleId} options={data.articles} onChange={articleId=>setF({...f,articleId})}/></F>
      <F l={t("subcontracting.unit")}><select value={f.unitOfMeasureId} onChange={e=>setF({...f,unitOfMeasureId:e.target.value})}><option value="">—</option>{data.units.map(x=><option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}</select></F>
      <N l={t("subcontracting.quantityMultiple")} v={f.quantityMultiple} s={quantityMultiple=>setF({...f,quantityMultiple})}/><N l={t("subcontracting.optimalQuantity")} v={f.optimalQuantity} s={optimalQuantity=>setF({...f,optimalQuantity})}/>
      <N l={t("subcontracting.unitCost")} v={f.unitCost} s={unitCost=>setF({...f,unitCost})}/><N l={t("subcontracting.leadTimeDays")} v={f.leadTimeDays} s={leadTimeDays=>setF({...f,leadTimeDays})}/>
      <label className="checkbox-row"><input type="checkbox" checked={f.deliveryControl} onChange={e=>setF({...f,deliveryControl:e.target.checked})}/><span>{t("subcontracting.deliveryControl")}</span></label>
      <label className="checkbox-row"><input type="checkbox" checked={f.isActive} onChange={e=>setF({...f,isActive:e.target.checked})}/><span>{t("administration.active")}</span></label>
    </div></IntegrationProtectionBoundary><div className="form-actions"><button className="primary-button">{t("administration.save")}</button></div>
  </form>
}
function OrderLineEditor({data,headerId,item,save,close}:{data:SubcontractingSnapshot;headerId:string;item?:ExternalOrderLine;save:(x:unknown)=>Promise<boolean>;close:()=>void}){
  const {t}=useLocale();
  const [f,setF]=useState({externalProductionOrderId:headerId,lineNumber:item?.lineNumber??"",articleId:item?.articleId??"",productionOrderId:item?.productionOrderId??"",productionOrderOperationId:item?.productionOrderOperationId??"",orderedQuantity:item?.orderedQuantity??1,dispatchedQuantity:item?.dispatchedQuantity??0,receivedQuantity:item?.receivedQuantity??0,acceptedQuantity:item?.acceptedQuantity??0,rejectedQuantity:item?.rejectedQuantity??0,requiredAtUtc:(item?.requiredAtUtc??new Date().toISOString()).slice(0,16),plannedDispatchAtUtc:item?.plannedDispatchAtUtc?.slice(0,16)??"",plannedReceiptAtUtc:item?.plannedReceiptAtUtc?.slice(0,16)??"",actualDispatchAtUtc:item?.actualDispatchAtUtc?.slice(0,16)??"",actualReceiptAtUtc:item?.actualReceiptAtUtc?.slice(0,16)??"",plannedCost:item?.plannedCost??0,actualCost:item?.actualCost??0,externalReference:item?.externalReference??"",notes:item?.notes??""});
  const operations=data.operations.filter(x=>!f.productionOrderId||x.productionOrderId===f.productionOrderId);
  const dt=(v:string)=>(v?new Date(v).toISOString():null);
  return <form className="administration-form related-record-editor inventory-inline-form" onSubmit={async e=>{e.preventDefault();if(await save({...f,lineNumber:item?null:f.lineNumber,productionOrderId:f.productionOrderId||null,productionOrderOperationId:f.productionOrderOperationId||null,requiredAtUtc:dt(f.requiredAtUtc),plannedDispatchAtUtc:dt(f.plannedDispatchAtUtc),plannedReceiptAtUtc:dt(f.plannedReceiptAtUtc),actualDispatchAtUtc:dt(f.actualDispatchAtUtc),actualReceiptAtUtc:dt(f.actualReceiptAtUtc)}))close()}}>
    <div className="section-heading"><h3>{t(item?"subcontracting.editLine":"subcontracting.newLine")}</h3><button type="button" className="secondary-button" onClick={close}>{t("administration.cancel")}</button></div>
    <IntegrationProtectionBoundary entity="ExternalProductionOrderLine" fields={['lineNumber','articleCode','productionOrderNumber','operationSequence','orderedQuantity','requiredAtUtc','plannedDispatchAtUtc','plannedReceiptAtUtc','dispatchedQuantity','receivedQuantity','acceptedQuantity','rejectedQuantity','actualDispatchAtUtc','actualReceiptAtUtc','plannedCost','actualCost','externalReference','notes']}><div className="compact-form-grid">
      <F l={t("subcontracting.lineNumber")}><input required disabled={!!item} value={f.lineNumber} onChange={e=>setF({...f,lineNumber:e.target.value})}/></F>
      <F l={t("materials.article")}><EntityPicker label={t("materials.article")} value={f.articleId} options={data.articles} onChange={articleId=>setF({...f,articleId})}/></F>
      <F l={t("subcontracting.productionOrder")}><EntityPicker label={t("subcontracting.productionOrder")} value={f.productionOrderId} options={data.productionOrders.map(x=>({id:x.id,code:x.number,name:`${x.articleCode} · ${x.articleName}`}))} onChange={productionOrderId=>setF({...f,productionOrderId,productionOrderOperationId:""})}/></F>
      <F l={t("subcontracting.operation")}><EntityPicker label={t("subcontracting.operation")} value={f.productionOrderOperationId} options={operations.map(x=>({id:x.id,code:String(x.sequence),name:`${x.code} · ${x.name}`}))} onChange={productionOrderOperationId=>setF({...f,productionOrderOperationId})}/></F>
      <N l={t("materials.quantity")} v={f.orderedQuantity} s={orderedQuantity=>setF({...f,orderedQuantity})}/><F l={t("subcontracting.requiredAt")}><input required type="datetime-local" value={f.requiredAtUtc} onChange={e=>setF({...f,requiredAtUtc:e.target.value})}/></F>
      <F l={t("subcontracting.plannedDispatch")}><input type="datetime-local" value={f.plannedDispatchAtUtc} onChange={e=>setF({...f,plannedDispatchAtUtc:e.target.value})}/></F><F l={t("subcontracting.plannedReceipt")}><input type="datetime-local" value={f.plannedReceiptAtUtc} onChange={e=>setF({...f,plannedReceiptAtUtc:e.target.value})}/></F>
      <N l={t("subcontracting.dispatchedQuantity")} v={f.dispatchedQuantity} s={dispatchedQuantity=>setF({...f,dispatchedQuantity})}/><N l={t("subcontracting.receivedQuantity")} v={f.receivedQuantity} s={receivedQuantity=>setF({...f,receivedQuantity})}/><N l={t("subcontracting.acceptedQuantity")} v={f.acceptedQuantity} s={acceptedQuantity=>setF({...f,acceptedQuantity})}/><N l={t("subcontracting.rejectedQuantity")} v={f.rejectedQuantity} s={rejectedQuantity=>setF({...f,rejectedQuantity})}/>
      <F l={t("subcontracting.actualDispatch")}><input type="datetime-local" value={f.actualDispatchAtUtc} onChange={e=>setF({...f,actualDispatchAtUtc:e.target.value})}/></F><F l={t("subcontracting.actualReceipt")}><input type="datetime-local" value={f.actualReceiptAtUtc} onChange={e=>setF({...f,actualReceiptAtUtc:e.target.value})}/></F>
      <N l={t("subcontracting.plannedCost")} v={f.plannedCost} s={plannedCost=>setF({...f,plannedCost})}/><N l={t("subcontracting.actualCost")} v={f.actualCost} s={actualCost=>setF({...f,actualCost})}/><F l={t("subcontracting.externalReference")}><input value={f.externalReference} onChange={e=>setF({...f,externalReference:e.target.value})}/></F><F l={t("subcontracting.notes")}><textarea value={f.notes} onChange={e=>setF({...f,notes:e.target.value})}/></F>
    </div></IntegrationProtectionBoundary><div className="form-actions"><button className="primary-button">{t("administration.save")}</button></div>
  </form>
}
function RelatedRecordsPanel({title,action,children}:{title:string;action:ReactNode;children:ReactNode}){
  return <section className="data-panel administration-panel inventory-detail-panel related-records-panel">
    <div className="section-heading"><h2>{title}</h2>{action}</div>
    {children}
  </section>;
}
function DetailTabs<T extends string>({value,onChange,relatedValue,relatedLabel,relatedCount,relatedDisabled}:{value:T;onChange:(value:T)=>void;relatedValue:T;relatedLabel:string;relatedCount:number;relatedDisabled:boolean}){
  const {t}=useLocale();
  const details="details" as T;
  return <nav className="administration-tabs inventory-tabs subcontracting-detail-tabs" role="tablist">
    <button type="button" role="tab" aria-selected={value===details} className={value===details?"active":""} onClick={()=>onChange(details)}>{t("subcontracting.details")}</button>
    <button type="button" role="tab" aria-selected={value===relatedValue} className={value===relatedValue?"active":""} disabled={relatedDisabled} onClick={()=>onChange(relatedValue)}>{relatedLabel} ({relatedCount})</button>
  </nav>;
}
function EmptyRelatedRow({columns}:{columns:number}){
  const {t}=useLocale();
  return <tr className="subcontracting-empty-row"><td className="empty-state" colSpan={columns}>{t("administration.empty")}</td></tr>;
}
function RelatedPager({page,total,setPage}:{page:number;total:number;setPage:(page:number)=>void}){
  const {t,formatNumber}=useLocale();
  const pages=Math.max(1,Math.ceil(total/10));
  if(total<=10)return null;
  return <footer className="table-pagination inventory-fixed-pagination">
    <span className="record-list-pagination-summary">{t("recordList.pageOf").replace("{page}",formatNumber(page)).replace("{total}",formatNumber(pages))}{" · "}{t("recordList.totalRecords")}: {formatNumber(total)}</span>
    <div className="record-list-pagination-actions">
      <button className="secondary-button" type="button" disabled={page<=1} onClick={()=>setPage(page-1)}>{t("recordList.previousPage")}</button>
      <button className="secondary-button" type="button" disabled={page>=pages} onClick={()=>setPage(page+1)}>{t("recordList.nextPage")}</button>
    </div>
  </footer>;
}
function F({ l, children }: { l: string; children: ReactNode }) {
  return (
    <label>
      <span>{l}</span>
      {children}
    </label>
  );
}
function N({ l, v, s }: { l: string; v: number; s: (x: number) => void }) {
  return (
    <F l={l}>
      <input
        type="number"
        min="0"
        step="any"
        value={v}
        onChange={(e) => s(Number(e.target.value))}
      />
    </F>
  );
}
